import { isNullOrUndefined } from '../../shared/utils';

declare global {
  type MapValueFactory<K, V> = (() => V) | ((key: K) => V);

  interface Map<K, V> {
    getOrAdd(key: K, valueToAdd: V): V;
    getOrCreate(key: K, factory: MapValueFactory<K, V>): V;

    isTreadingUndefinedAsNull(): boolean;
    withTreatUndefinedAsNull(): Map<K, V>;

    withDefaultFactory(factory: MapValueFactory<K, V>): MapWithDefaultFactory<K, V>;
  }

  interface MapWithDefaultFactory<K, V> extends Map<K, V> {
    withTreatUndefinedAsNull(): MapWithDefaultFactory<K, V>;

    get defaultFactory(): MapValueFactory<K, V>;
    set defaultFactory(value: MapValueFactory<K, V>);

    getOrCreate(key: K): V;
  }
}

Map.prototype.getOrAdd = function (this: Map<any, any>, key: any, valueToAdd: any) {
  if (!this.has(key))
    this.set(key, valueToAdd);

  return this.get(key);
};

Map.prototype.getOrCreate = function (this: Map<any, any>, key: any, factory: any) {
  if (!this.has(key)) {
    if (!factory)
      factory = this['defaultFactory'];

    this.set(key, factory(key));
  }

  return this.get(key);
};

const _get    = Map.prototype.get;
const _set    = Map.prototype.set;
const _delete = Map.prototype.delete;
const _has    = Map.prototype.has;

const SYMBOL_isTreadingUndefinedAsNull = Symbol('isTreadingUndefinedAsNull');

Map.prototype.isTreadingUndefinedAsNull = function (this: Map<any, any>) {
  return SYMBOL_isTreadingUndefinedAsNull in this;
}

Map.prototype.withTreatUndefinedAsNull = function (this: Map<any, any>) {
  if (this.isTreadingUndefinedAsNull())
    return this;

  this.get = function (this: Map<any, any>, key: any) {
    key ??= null;
    return _get.call(this, key);
  };

  this.set = function (this: Map<any, any>, key: any, value: any) {
    key ??= null;
    return _set.call(this, key, value);
  };

  this.delete = function (this: Map<any, any>, key: any) {
    key ??= null;
    return _delete.call(this, key);
  };

  this.has = function (this: Map<any, any>, key: any) {
    key ??= null;
    return _has.call(this, key);
  };

  return this;
};

Map.prototype.withDefaultFactory = function (this: Map<any, any>, factory: MapValueFactory<any, any>) {
  this['defaultFactory'] = factory;
  return <any>this;
};

export {};
